import React from 'react';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import CommunicationService from '../services/CommunicationService';

interface EventShareButtonsProps {
  event: {
    title: string;
    date: string;
    time: string;
    venue: string;
  };
  bookingUrl?: string;
  size?: 'sm' | 'lg';
  variant?: 'primary' | 'success' | 'outline-primary' | 'outline-success';
  className?: string;
}

/**
 * Event Share Buttons Component
 * Allows sharing event details via multiple channels using backend templates
 */
const EventShareButtons: React.FC<EventShareButtonsProps> = ({
  event,
  bookingUrl,
  size,
  variant = 'outline-success',
  className = ''
}) => {

  const handleShare = async (channel: 'email' | 'sms' | 'whatsapp') => {
    try {
      // Check if this is group sharing (when event.venue contains "theater group")
      if (event.venue.toLowerCase().includes('theater group') || event.venue.toLowerCase().includes('theatre group')) {
        await CommunicationService.shareGroup(
          event.title, // groupName
          event.time, // groupLocation
          event.date, // groupEstablished
          event.venue, // groupDescription
          bookingUrl || window.location.href, // groupUrl
          channel
        );
      } else {
        await CommunicationService.shareEvent(
          event.title,
          event.date,
          event.time,
          event.venue,
          bookingUrl || window.location.href,
          undefined, // recipientPhone - will be filled by user
          channel
        );
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          text: `Join me for ${event.title} on ${event.date} at ${event.time}. Venue: ${event.venue}`,
          url: bookingUrl || window.location.href
        });
      } catch (error) {
        console.error('Error sharing:', error);
        // Fallback to WhatsApp
        handleShare('whatsapp');
      }
    } else {
      // Fallback to WhatsApp
      handleShare('whatsapp');
    }
  };

  const copyToClipboard = async () => {
    const shareText = `🎭 ${event.title}\n\n📅 Date: ${event.date}\n⏰ Time: ${event.time}\n📍 Venue: ${event.venue}\n\n🎫 Book tickets: ${bookingUrl || window.location.href}\n\nJoin me for this amazing show!`;
    
    try {
      await navigator.clipboard.writeText(shareText);
      // You might want to show a toast notification here
      console.log('Event details copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <Dropdown as={ButtonGroup} className={className}>
      <Button
        variant={variant}
        size={size}
        onClick={handleNativeShare}
      >
        <i className="bi bi-share me-1"></i>
        Share Now
      </Button>
      
      <Dropdown.Toggle split variant={variant} size={size} />
      
      <Dropdown.Menu>
        <Dropdown.Item onClick={() => handleShare('whatsapp')}>
          <i className="bi bi-whatsapp text-success me-2"></i>
          WhatsApp
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleShare('sms')}>
          <i className="bi bi-chat-dots text-primary me-2"></i>
          SMS
        </Dropdown.Item>
        <Dropdown.Item onClick={() => handleShare('email')}>
          <i className="bi bi-envelope text-warning me-2"></i>
          Email
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={copyToClipboard}>
          <i className="bi bi-clipboard text-secondary me-2"></i>
          Copy Details
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default EventShareButtons;